// Matrix editor for `Model.levelWeights`. Rows = (discipline, channel), columns = L1..L8.
// Shows a row-sum indicator that turns green when sums to 100%.

import { useMemo } from 'react';
import clsx from 'clsx';
import { SENIORITY_TILT_STRENGTH } from '@rc/engine';
import type { LevelId, LevelWeightRow, Model } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function LevelWeightsMatrix({ model, onChange }: Props) {
  const rows = model.levelWeights || [];
  const levels = model.levels || [];

  const channelLabel = (id: string) =>
    id === 'all' ? 'All channels' : (model.channels || []).find((c) => c.id === id)?.label || id;
  const disciplineLabel = (id: string) =>
    (model.disciplines || []).find((d) => d.id === id)?.label || id;

  const updateRow = (i: number, weights: Partial<Record<LevelId, number>>) => {
    const next = [...rows];
    next[i] = { ...next[i], weights };
    onChange({ ...model, levelWeights: next });
  };

  const tiltStrength = model.seniorityTiltStrength;
  const effectiveTilt = tiltStrength === undefined ? SENIORITY_TILT_STRENGTH : tiltStrength;
  const setTilt = (raw: string) => {
    if (raw.trim() === '') {
      // Empty = revert to engine default.
      const next = { ...model };
      delete next.seniorityTiltStrength;
      onChange(next);
      return;
    }
    const n = Math.max(0, Math.min(1, Number(raw) || 0));
    onChange({ ...model, seniorityTiltStrength: n });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Level weights distribute the total FTE for a (discipline × channel) cell across L1..L8.
        Rows should sum to <strong>100%</strong>. Missing levels are treated as 0%.
      </p>

      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="min-w-0">
            <label className="label !mb-0.5">Seniority tilt strength</label>
            <p className="text-xs text-slate-500 max-w-md">
              Controls how far the scenario Leveling slider shifts the mix at its extremes.
              Higher = more aggressive; <strong>0</strong> disables the tilt. Engine default is{' '}
              <span className="tabular-nums">{SENIORITY_TILT_STRENGTH}</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step={0.01}
              min={0}
              max={1}
              className="input py-1 px-2 w-24 text-right tabular-nums"
              value={tiltStrength ?? ''}
              placeholder={String(SENIORITY_TILT_STRENGTH)}
              onChange={(e) => setTilt(e.target.value)}
            />
            {tiltStrength === undefined ? (
              <span className="text-[11px] text-slate-400">default</span>
            ) : (
              <button
                type="button"
                className="text-[11px] text-brand-purple-600 hover:underline"
                onClick={() => setTilt('')}
              >
                Reset to default
              </button>
            )}
          </div>
          <span className="text-[11px] text-slate-400 tabular-nums">
            in effect: {effectiveTilt}
          </span>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Discipline</th>
              <th className="text-left px-3 py-2">Channel</th>
              {levels.map((l) => (
                <th key={l.id} className="text-right px-3 py-2 tabular-nums">
                  {l.id}
                </th>
              ))}
              <th className="text-right px-3 py-2">Sum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <Row
                key={i}
                row={r}
                levels={levels.map((l) => l.id)}
                disciplineLabel={disciplineLabel(r.disciplineId)}
                channelLabel={channelLabel(r.channelId)}
                onChange={(w) => updateRow(i, w)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({
  row,
  levels,
  disciplineLabel,
  channelLabel,
  onChange,
}: {
  row: LevelWeightRow;
  levels: LevelId[];
  disciplineLabel: string;
  channelLabel: string;
  onChange: (w: Partial<Record<LevelId, number>>) => void;
}) {
  const sum = useMemo(
    () => Object.values(row.weights || {}).reduce<number>((s, v) => s + (Number(v) || 0), 0),
    [row.weights],
  );
  const ok = Math.abs(sum - 1) < 0.005;

  const set = (l: LevelId, n: number) => {
    onChange({ ...row.weights, [l]: n });
  };

  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-1.5 text-slate-700">{disciplineLabel}</td>
      <td className="px-3 py-1.5 text-slate-500 text-xs">{channelLabel}</td>
      {levels.map((l) => (
        <td key={l} className="px-1 py-1 align-middle min-w-[78px]">
          <input
            type="number"
            step={0.01}
            min={0}
            max={1}
            className="input py-1 px-2 w-full text-right text-xs tabular-nums"
            value={row.weights?.[l] ?? ''}
            onChange={(e) => set(l, e.target.value === '' ? 0 : Number(e.target.value))}
            placeholder="—"
          />
        </td>
      ))}
      <td
        className={clsx(
          'px-3 py-1.5 text-right tabular-nums text-xs',
          ok ? 'text-emerald-600' : 'text-rose-600',
        )}
      >
        {(sum * 100).toFixed(1)}%
      </td>
    </tr>
  );
}
